/**
 * OrgResolver - определение организации из домена, параметра или конфигурации
 *
 * Порядок приоритетов:
 * 1. URL параметр ?org=xxx (для dev/testing)
 * 2. Subdomain (university.tlh.ru → university)
 * 3. Full domain mapping (custom-domain.com → org)
 * 4. iframe config (postMessage)
 * 5. Default (www.tlh.ru)
 */

import type { OrgResolution } from './types'

/**
 * Конфигурация OrgResolver
 */
export interface OrgResolverConfig {
  /** Базовые домены (tlh.ru, localhost) */
  baseDomains: string[]
  /** Маппинг полных доменов на org slug */
  domainMapping?: Record<string, string>
  /** Org по умолчанию */
  defaultOrg: string
  /** Разрешить определение из URL параметра */
  allowUrlParam?: boolean
}

/**
 * Дефолтная конфигурация
 */
const defaultConfig: OrgResolverConfig = {
  baseDomains: ['tlh.ru', 'localhost', '127.0.0.1'],
  domainMapping: {},
  defaultOrg: 'www.tlh.ru',
  allowUrlParam: true,
}

/**
 * Класс для определения организации
 */
export class OrgResolver {
  private config: OrgResolverConfig

  constructor(config: Partial<OrgResolverConfig> = {}) {
    this.config = { ...defaultConfig, ...config }
  }

  /**
   * Определить организацию
   * @returns slug организации и источник определения
   */
  resolve(): OrgResolution {
    // 1. URL параметр (приоритет для dev/testing)
    if (this.config.allowUrlParam) {
      const fromParam = this.resolveFromUrlParam()
      if (fromParam) return fromParam
    }

    // 2. Subdomain (university.tlh.ru → university)
    const fromSubdomain = this.resolveFromSubdomain()
    if (fromSubdomain) return fromSubdomain

    // 3. Full domain mapping
    const fromDomain = this.resolveFromDomainMapping()
    if (fromDomain) return fromDomain

    // 4. iframe config (проверяем если в iframe)
    const fromIframe = this.resolveFromIframeConfig()
    if (fromIframe) return fromIframe

    // 5. Default
    return {
      slug: this.config.defaultOrg,
      source: 'default',
    }
  }

  /**
   * Определение из URL параметра ?org=xxx
   */
  private resolveFromUrlParam(): OrgResolution | null {
    try {
      const params = new URLSearchParams(window.location.search)
      const orgParam = params.get('org')

      if (orgParam && this.isValidSlug(orgParam)) {
        return {
          slug: orgParam,
          source: 'param',
        }
      }
    } catch (e) {
      // В случае ошибки (например, SSR) продолжаем
      console.warn('[OrgResolver] Error reading URL param:', e)
    }

    return null
  }

  /**
   * Определение из subdomain
   * university.tlh.ru → university
   * www.tlh.ru → null (www игнорируется)
   */
  private resolveFromSubdomain(): OrgResolution | null {
    try {
      const hostname = window.location.hostname
      const subdomain = this.extractSubdomain(hostname)

      if (subdomain && subdomain !== 'www' && this.isValidSlug(subdomain)) {
        return {
          slug: subdomain,
          source: 'subdomain',
        }
      }
    } catch {
      // В случае ошибки продолжаем
    }

    return null
  }

  /**
   * Извлечь subdomain из hostname
   * university.tlh.ru → university
   * tlh.ru → null
   * localhost → null
   */
  private extractSubdomain(hostname: string): string | null {
    // Проверяем каждый базовый домен
    for (const baseDomain of this.config.baseDomains) {
      // Точное совпадение с базовым доменом
      if (hostname === baseDomain) {
        return null
      }

      // Проверяем subdomain.baseDomain
      if (hostname.endsWith(`.${baseDomain}`)) {
        const subdomain = hostname.slice(0, -(baseDomain.length + 1))
        // Возвращаем только первый уровень subdomain
        const parts = subdomain.split('.')
        return parts[parts.length - 1]
      }
    }

    return null
  }

  /**
   * Определение из полного домена через маппинг
   * custom-domain.com → mapped-org
   */
  private resolveFromDomainMapping(): OrgResolution | null {
    if (!this.config.domainMapping) return null

    try {
      const hostname = window.location.hostname
      const mappedSlug = this.config.domainMapping[hostname]

      if (mappedSlug) {
        return {
          slug: mappedSlug,
          source: 'domain',
        }
      }
    } catch {
      // В случае ошибки продолжаем
    }

    return null
  }

  /**
   * Определение из конфигурации iframe (postMessage)
   */
  private resolveFromIframeConfig(): OrgResolution | null {
    // Проверяем, находимся ли в iframe
    if (!this.isInIframe()) return null

    // Проверяем window.SHELL_CONFIG (может быть установлен родителем)
    try {
      const shellConfig = (window as Window & { SHELL_CONFIG?: { org?: string } }).SHELL_CONFIG
      if (shellConfig?.org && this.isValidSlug(shellConfig.org)) {
        return {
          slug: shellConfig.org,
          source: 'config',
        }
      }
    } catch {
      // В случае ошибки продолжаем
    }

    return null
  }

  /**
   * Проверить, находимся ли в iframe
   */
  private isInIframe(): boolean {
    try {
      return window.self !== window.top
    } catch {
      // Если cross-origin, скорее всего в iframe
      return true
    }
  }

  /**
   * Валидация slug
   * Разрешены: a-z, 0-9, дефис
   */
  private isValidSlug(slug: string): boolean {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(slug)
  }

  /**
   * Обновить конфигурацию
   */
  updateConfig(config: Partial<OrgResolverConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Добавить маппинг домена
   */
  addDomainMapping(domain: string, orgSlug: string): void {
    if (!this.config.domainMapping) {
      this.config.domainMapping = {}
    }
    this.config.domainMapping[domain] = orgSlug
  }

  /**
   * Получить текущую конфигурацию
   */
  getConfig(): OrgResolverConfig {
    return { ...this.config }
  }
}

// Singleton instance
let resolverInstance: OrgResolver | null = null

/**
 * Получить singleton экземпляр OrgResolver
 */
export function getOrgResolver(config?: Partial<OrgResolverConfig>): OrgResolver {
  if (!resolverInstance) {
    resolverInstance = new OrgResolver(config)
  } else if (config) {
    resolverInstance.updateConfig(config)
  }
  return resolverInstance
}

/**
 * Создать новый экземпляр OrgResolver (для тестирования)
 */
export function createOrgResolver(config?: Partial<OrgResolverConfig>): OrgResolver {
  return new OrgResolver(config)
}

export default OrgResolver
